import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Shield, Users, PawPrint, CreditCard, Search, Pause, Play, Trash2, Edit, LogOut,
  Plus, Building2, Stethoscope, UserPlus, Link2, Activity, Cpu, RefreshCw, Lightbulb, CheckCircle, XCircle, Gift, Ban, Mail, Eye
} from 'lucide-react';

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [pets, setPets] = useState([]);
  const [microchips, setMicrochips] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [vets, setVets] = useState([]);
  const [breeders, setBreeders] = useState([]);
  const [shelters, setShelters] = useState([]);
  const [owners, setOwners] = useState([]);
  const [partners, setPartners] = useState([]);
  const [featureRequests, setFeatureRequests] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [createPetDialogOpen, setCreatePetDialogOpen] = useState(false);
  const [connectPetDialogOpen, setConnectPetDialogOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  
  // NEW: Search states
  const [ownerSearchQuery, setOwnerSearchQuery] = useState('');
  const [ownerSearchResults, setOwnerSearchResults] = useState([]);
  const [petSearchQuery, setPetSearchQuery] = useState('');
  const [petSearchResults, setPetSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [createNewOwner, setCreateNewOwner] = useState(false);
  
  // Log search and filter states
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [logRoleFilter, setLogRoleFilter] = useState('all');
  const [logActionFilter, setLogActionFilter] = useState('all');
  const [logsSummary, setLogsSummary] = useState(null);
  
  // User pets dialog
  const [userPetsDialogOpen, setUserPetsDialogOpen] = useState(false);
  const [selectedUserPets, setSelectedUserPets] = useState({ pets: [], shelter_animals: [], breeder_animals: [] });
  const [selectedUserForPets, setSelectedUserForPets] = useState(null);
  const [editPetDialogOpen, setEditPetDialogOpen] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  
  // Send login email dialog (mailto: approach)
  const [loginEmailDialogOpen, setLoginEmailDialogOpen] = useState(false);
  const [loginEmailData, setLoginEmailData] = useState(null);
  
  // Form states
  const [newUser, setNewUser] = useState({
    email: '', password: '', name: '', role: 'shelter', phone: '',
    address: '', city: '', state: '', zip_code: '',
    organization_name: '', license_number: '', clinic_name: '',
    business_name: '', registration_number: '', breeds_specialized: '',
    specializations: '', can_implant_microchips: true,
    send_email: true  // Default to sending email
  });
  
  const [newPet, setNewPet] = useState({
    name: '', species: 'Dog', breed: '', gender: '', color: '',
    date_of_birth: '', microchip_number: '', secondary_microchip: '',
    owner_id: '', owner_type: 'owner', breeder_id: '', implanter_id: '',
    implant_date: '', status: 'owned'
  });
  
  // NEW: New owner form state (for inline creation)
  const [newOwnerForm, setNewOwnerForm] = useState({
    name: '', email: '', phone: '', password: 'tempPassword123!',
    address: '', city: '', state: '', zip_code: ''
  });
  
  const [petConnection, setPetConnection] = useState({
    owner_id: '', owner_type: 'owner', breeder_id: '', implanter_id: '',
    implant_date: '', vet_id: ''
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get('/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
  };

  const fetchUsers = async () => {
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get('/admin/users', { params });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchAnimals = async () => {
    try {
      const response = await axios.get('/admin/animals');
      setAnimals(response.data);
    } catch (error) {
      toast.error('Failed to load animals');
    }
  };

  const fetchPets = async () => {
    try {
      const response = await axios.get('/admin/pets');
      // Handle both paginated response and direct array response
      const petsData = response.data.animals || response.data || [];
      setPets(Array.isArray(petsData) ? petsData : []);
    } catch (error) {
      toast.error('Failed to load pets');
    }
  };

  const fetchMicrochips = async () => {
    try {
      const response = await axios.get('/admin/microchips');
      setMicrochips(response.data);
    } catch (error) {
      toast.error('Failed to load microchips');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get('/admin/transactions');
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    }
  };

  const fetchActivityLogs = async (search = '', role = 'all', action = 'all') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (role && role !== 'all') params.append('user_role', role);
      if (action && action !== 'all') params.append('action_type', action);
      params.append('limit', '200');
      
      const response = await axios.get(`/admin/activity-logs?${params.toString()}`);
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Failed to load logs');
    }
  };

  const fetchLogsSummary = async () => {
    try {
      const response = await axios.get('/admin/logs/summary');
      setLogsSummary(response.data);
    } catch (error) {
      console.error('Failed to load logs summary');
    }
  };

  const handleSearchLogs = () => {
    fetchActivityLogs(logSearchQuery, logRoleFilter, logActionFilter);
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get('/admin/partners');
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to load partners');
    }
  };

  const fetchFeatureRequests = async () => {
    try {
      const response = await axios.get('/admin/feature-requests');
      setFeatureRequests(response.data);
    } catch (error) {
      console.error('Failed to load feature requests');
    }
  };

  const handleApprovePartner = async (partnerId) => {
    try {
      await axios.put(`/admin/partners/${partnerId}/approve`);
      toast.success('Partner approved!');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to approve partner');
    }
  };

  const handleSuspendPartner = async (partnerId) => {
    try {
      await axios.put(`/admin/partners/${partnerId}/suspend`);
      toast.success('Partner suspended');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to suspend partner');
    }
  };

  // Free Access Handlers
  const handleGrantFreeAccess = async (userId, durationMonths = null, reason = '') => {
    try {
      await axios.put(`/admin/users/${userId}/grant-free-access`, {
        duration_months: durationMonths,
        reason: reason || 'Granted by admin'
      });
      toast.success('Free premium access granted!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant free access');
    }
  };

  const handleRevokeFreeAccess = async (userId) => {
    try {
      await axios.put(`/admin/users/${userId}/revoke-free-access`);
      toast.success('Free access revoked');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to revoke free access');
    }
  };

  const handleGrantPartnerFreeAccess = async (partnerId, durationMonths = null, reason = '') => {
    try {
      await axios.put(`/admin/partners/${partnerId}/grant-free-access`, {
        duration_months: durationMonths,
        reason: reason || 'Granted by admin'
      });
      toast.success('Free partner access granted!');
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant free access');
    }
  };

  const handleRevokePartnerFreeAccess = async (partnerId) => {
    try {
      await axios.put(`/admin/partners/${partnerId}/revoke-free-access`);
      toast.success('Free access revoked');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to revoke free access');
    }
  };

  const handleUpdateFeatureRequest = async (requestId, status) => {
    try {
      await axios.put(`/admin/feature-requests/${requestId}`, { status });
      toast.success('Request updated!');
      fetchFeatureRequests();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const fetchSelectionData = async () => {
    try {
      const [vetsRes, breedersRes, sheltersRes, ownersRes] = await Promise.all([
        axios.get('/admin/vets'),
        axios.get('/admin/breeders'),
        axios.get('/admin/shelters'),
        axios.get('/admin/owners')
      ]);
      setVets(vetsRes.data || []);
      setBreeders(breedersRes.data || []);
      setShelters(sheltersRes.data || []);
      setOwners(ownersRes.data || []);
    } catch (error) {
      console.error('Failed to load selection data');
    }
  };

  // NEW: Search owners by name/email/phone
  const searchOwners = async (query) => {
    if (!query || query.length < 2) {
      setOwnerSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await axios.get('/admin/search/users', { 
        params: { query, role: 'owner' } 
      });
      setOwnerSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Owner search failed:', error);
      setOwnerSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // NEW: Search all users by name/email/phone
  const searchAllUsers = async (query, role = null) => {
    if (!query || query.length < 2) {
      setOwnerSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const params = { query };
      if (role) params.role = role;
      const response = await axios.get('/admin/search/users', { params });
      setOwnerSearchResults(response.data.results || []);
    } catch (error) {
      console.error('User search failed:', error);
      setOwnerSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // NEW: Search pets by microchip
  const searchPets = async (query) => {
    if (!query || query.length < 2) {
      setPetSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await axios.get('/admin/search/pets', { 
        params: { query, search_by: 'microchip' } 
      });
      setPetSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Pet search failed:', error);
      setPetSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search effect for owner search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (ownerSearchQuery) {
        searchOwners(ownerSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [ownerSearchQuery]);

  // Debounced search effect for pet search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (petSearchQuery) {
        searchPets(petSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [petSearchQuery]);

  const handlePauseAccount = async (userId) => {
    try {
      await axios.put(`/admin/users/${userId}/pause`);
      toast.success('Account paused');
      fetchUsers();
      fetchActivityLogs();
    } catch (error) {
      toast.error('Failed to pause account');
    }
  };

  const handleActivateAccount = async (userId) => {
    try {
      await axios.put(`/admin/users/${userId}/activate`);
      toast.success('Account activated');
      fetchUsers();
      fetchActivityLogs();
    } catch (error) {
      toast.error('Failed to activate account');
    }
  };

  const handleDeleteAccount = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    
    try {
      await axios.delete(`/admin/users/${userId}`);
      toast.success('Account deleted');
      fetchUsers();
      fetchActivityLogs();
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/admin/users/${selectedUser.user_id}`, selectedUser);
      toast.success('User updated');
      setEditDialog(false);
      fetchUsers();
      fetchActivityLogs();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/admin/create-user', newUser);
      
      // If send_email was checked, open mailto: dialog
      if (response.data.email_data) {
        setLoginEmailData(response.data.email_data);
        setLoginEmailDialogOpen(true);
        toast.success(`${newUser.role} created! Now send the login credentials via email.`);
      } else {
        toast.success(`${newUser.role} created successfully!`);
        if (response.data.temp_password) {
          toast.info(`Temp password: ${response.data.temp_password}`, { duration: 10000 });
        }
      }
      
      setCreateUserDialogOpen(false);
      setNewUser({
        email: '', password: '', name: '', role: 'shelter', phone: '',
        address: '', city: '', state: '', zip_code: '',
        organization_name: '', license_number: '', clinic_name: '',
        business_name: '', registration_number: '', breeds_specialized: '',
        specializations: '', can_implant_microchips: true,
        send_email: true
      });
      fetchUsers();
      fetchSelectionData();
      fetchActivityLogs();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleViewUserPets = async (user) => {
    setSelectedUserForPets(user);
    try {
      const response = await axios.get(`/admin/users/${user.user_id}/pets`);
      setSelectedUserPets(response.data);
      setUserPetsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load user pets');
    }
  };

  const handleSendLoginEmail = async (user) => {
    try {
      const response = await axios.post(`/admin/send-login-email/${user.user_id}`);
      // Store the email data and open dialog with mailto: link
      setLoginEmailData(response.data);
      setLoginEmailDialogOpen(true);
      toast.success(`New credentials generated for ${user.email}`);
      fetchActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate credentials');
    }
  };
  
  // Generate mailto link for login credentials
  const getLoginMailtoLink = () => {
    if (!loginEmailData) return '';
    const subject = encodeURIComponent('International Pet Registry - Your Login Details');
    const body = encodeURIComponent(
      `Hello ${loginEmailData.user_name},\n\n` +
      `Your International Pet Registry account has been set up. Here are your login details:\n\n` +
      `Email: ${loginEmailData.user_email}\n` +
      `Temporary Password: ${loginEmailData.temp_password}\n\n` +
      `Please change your password immediately after logging in.\n\n` +
      `Login here: ${loginEmailData.login_url}\n\n` +
      `Best regards,\n` +
      `International Pet Registry Team`
    );
    return `mailto:${loginEmailData.user_email}?subject=${subject}&body=${body}`;
  };

  const handleEditPet = async (pet) => {
    setEditingPet(pet);
    setEditPetDialogOpen(true);
  };

  const handleSavePet = async (e) => {
    e.preventDefault();
    try {
      const petId = editingPet.pet_id || editingPet.animal_id;
      await axios.put(`/admin/pets/${petId}`, editingPet);
      toast.success('Pet updated successfully');
      setEditPetDialogOpen(false);
      // Refresh user pets
      if (selectedUserForPets) {
        const response = await axios.get(`/admin/users/${selectedUserForPets.user_id}/pets`);
        setSelectedUserPets(response.data);
      }
      fetchActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pet');
    }
  };

  const handleCreatePet = async (e) => {
    e.preventDefault();
    try {
      // Build payload - if creating new owner, include new_owner data
      const payload = {
        ...newPet
      };
      
      // If user chose to create a new owner inline
      if (createNewOwner && newPet.owner_type === 'owner') {
        payload.new_owner = newOwnerForm;
        delete payload.owner_id;  // Remove owner_id since we're creating a new one
      }
      
      // Use the new endpoint that supports inline owner creation
      await axios.post('/admin/create-pet-with-owner', payload);
      toast.success(createNewOwner ? 'Pet and Owner created successfully!' : 'Pet created with connections!');
      setCreatePetDialogOpen(false);
      setCreateNewOwner(false);
      setOwnerSearchQuery('');
      setOwnerSearchResults([]);
      setNewPet({
        name: '', species: 'Dog', breed: '', gender: '', color: '',
        date_of_birth: '', microchip_number: '', secondary_microchip: '',
        owner_id: '', owner_type: 'owner', breeder_id: '', implanter_id: '',
        implant_date: '', status: 'owned'
      });
      setNewOwnerForm({
        name: '', email: '', phone: '', password: 'tempPassword123!',
        address: '', city: '', state: '', zip_code: ''
      });
      fetchPets();
      fetchSelectionData();  // Refresh owners list
      fetchActivityLogs();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pet');
    }
  };

  const handleConnectPet = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/admin/pets/${selectedPet.pet_id}/connect`, petConnection);
      toast.success('Pet connections updated!');
      setConnectPetDialogOpen(false);
      setPetConnection({
        owner_id: '', owner_type: 'owner', breeder_id: '', implanter_id: '',
        implant_date: '', vet_id: ''
      });
      fetchPets();
      fetchActivityLogs();
    } catch (error) {
      toast.error('Failed to update pet connections');
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header data-testid="admin-dashboard" className="bg-primary text-white">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Panel</h1>
              <p className="text-sm text-white/80">{user.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchActivityLogs} variant="outline" className="text-white border-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={onLogout} variant="outline" className="text-white border-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-600">Total Users</p>
                    <p className="text-3xl font-bold">{stats.users.total}</p>
                  </div>
                  <Users className="w-10 h-10 text-primary opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-600">Shelters/Rescues</p>
                    <p className="text-3xl font-bold">{stats.users.shelters}</p>
                  </div>
                  <Building2 className="w-10 h-10 text-green-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-600">Vet Clinics</p>
                    <p className="text-3xl font-bold">{stats.users.vets}</p>
                  </div>
                  <Stethoscope className="w-10 h-10 text-blue-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-600">Breeders</p>
                    <p className="text-3xl font-bold">{stats.users.breeders}</p>
                  </div>
                  <PawPrint className="w-10 h-10 text-purple-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-600">Microchips</p>
                    <p className="text-3xl font-bold">{stats.animals.microchips_registered}</p>
                    <p className="text-xs text-stone-500">of {stats.animals.microchips_total}</p>
                  </div>
                  <Cpu className="w-10 h-10 text-orange-600 opacity-20" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions & Search */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions & Search</CardTitle>
            <CardDescription>Search owners/pets, add new users, and manage connections</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search Row */}
            <div className="grid md:grid-cols-2 gap-4 p-4 bg-stone-50 rounded-lg">
              {/* Owner Search */}
              <div>
                <Label className="text-sm font-medium mb-2 block">🔍 Search Owner (Name, Email, Phone)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter name, email or phone..."
                    value={ownerSearchQuery}
                    onChange={(e) => setOwnerSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => searchOwners(ownerSearchQuery)}
                    disabled={isSearching || !ownerSearchQuery}
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                {ownerSearchResults.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto border rounded-md bg-white">
                    {ownerSearchResults.map((o) => (
                      <div key={o.user_id} className="p-2 hover:bg-stone-100 border-b text-sm flex justify-between items-center">
                        <div>
                          <p className="font-medium">{o.name}</p>
                          <p className="text-xs text-stone-500">{o.email} | {o.phone || 'No phone'}</p>
                        </div>
                        <Badge variant="outline">{o.role}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                {ownerSearchQuery && ownerSearchResults.length === 0 && !isSearching && (
                  <p className="text-xs text-stone-500 mt-1">No results found. Try different search terms.</p>
                )}
              </div>
              
              {/* Pet Search by Microchip */}
              <div>
                <Label className="text-sm font-medium mb-2 block">🔍 Search Pet (Microchip Number)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter microchip number..."
                    value={petSearchQuery}
                    onChange={(e) => setPetSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => searchPets(petSearchQuery)}
                    disabled={isSearching || !petSearchQuery}
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                {petSearchResults.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto border rounded-md bg-white">
                    {petSearchResults.map((p) => (
                      <div key={p.pet_id} className="p-2 hover:bg-stone-100 border-b text-sm">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{p.name} - {p.species}</p>
                            <p className="text-xs text-stone-500 font-mono">{p.microchip_number}</p>
                          </div>
                          <Badge>{p.status}</Badge>
                        </div>
                        {p.owner && (
                          <p className="text-xs text-green-600 mt-1">Owner: {p.owner.name || 'Unknown'} ({p.owner.email || 'No email'})</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {petSearchQuery && petSearchResults.length === 0 && !isSearching && (
                  <p className="text-xs text-stone-500 mt-1">No pets found with this microchip.</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => { setCreateUserDialogOpen(true); fetchSelectionData(); }} className="bg-primary hover:bg-primary/90">
                <UserPlus className="w-4 h-4 mr-2" />Add Rescue/Vet/Breeder
              </Button>
              <Button variant="outline" onClick={() => { setCreatePetDialogOpen(true); fetchSelectionData(); setCreateNewOwner(false); }}>
                <PawPrint className="w-4 h-4 mr-2" />Add Pet with Connections
              </Button>
              <Button variant="outline" onClick={() => { fetchPets(); fetchActivityLogs(); }}>
                <Activity className="w-4 h-4 mr-2" />View Activity Logs
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs defaultValue="users" className="space-y-6" onValueChange={(value) => {
          if (value === 'users') fetchUsers();
          else if (value === 'animals') fetchAnimals();
          else if (value === 'pets') { fetchPets(); fetchSelectionData(); }
          else if (value === 'microchips') fetchMicrochips();
          else if (value === 'transactions') fetchTransactions();
          else if (value === 'partners') fetchPartners();
          else if (value === 'features') fetchFeatureRequests();
          else if (value === 'logs') { fetchActivityLogs(); fetchLogsSummary(); }
        }}>
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="animals">
              <PawPrint className="w-4 h-4 mr-2" />Animals
            </TabsTrigger>
            <TabsTrigger value="pets">
              <Link2 className="w-4 h-4 mr-2" />Pets
            </TabsTrigger>
            <TabsTrigger value="microchips">
              <Cpu className="w-4 h-4 mr-2" />Microchips
            </TabsTrigger>
            <TabsTrigger value="transactions">
              <CreditCard className="w-4 h-4 mr-2" />Transactions
            </TabsTrigger>
            <TabsTrigger value="partners">
              <Building2 className="w-4 h-4 mr-2" />Partners
            </TabsTrigger>
            <TabsTrigger value="features">
              <Lightbulb className="w-4 h-4 mr-2" />Features
            </TabsTrigger>
            <TabsTrigger value="logs">
              <Activity className="w-4 h-4 mr-2" />Logs
            </TabsTrigger>
          </TabsList>

          {/* USERS TAB */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>User Management</CardTitle>
                  <div className="flex gap-2">
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="shelter">Shelters</SelectItem>
                        <SelectItem value="vet">Vets</SelectItem>
                        <SelectItem value="breeder">Breeders</SelectItem>
                        <SelectItem value="owner">Owners</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-64"
                      />
                      <Button onClick={fetchUsers} className="bg-primary hover:bg-primary/90">
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-stone-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-semibold">{u.name}</p>
                            <p className="text-sm text-stone-600">{u.email}</p>
                            {(u.organization_name || u.clinic_name || u.business_name) && (
                              <p className="text-xs text-stone-500">{u.organization_name || u.clinic_name || u.business_name}</p>
                            )}
                            {u.created_by_admin && (
                              <Badge variant="outline" className="text-xs mt-1">Added by Admin</Badge>
                            )}
                            {/* Subscription Status for Shelters */}
                            {u.role === 'shelter' && (
                              <div className="flex gap-1 mt-1">
                                {u.free_access_granted ? (
                                  <Badge className="bg-green-100 text-green-800 text-xs">
                                    <Gift className="w-3 h-3 mr-1" />Free Access {u.free_access_unlimited ? '(Unlimited)' : ''}
                                  </Badge>
                                ) : u.subscription_status === 'trial' ? (
                                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">Trial</Badge>
                                ) : u.subscription_tier === 'premium' ? (
                                  <Badge className="bg-blue-100 text-blue-800 text-xs">Premium</Badge>
                                ) : (
                                  <Badge className="bg-stone-100 text-stone-600 text-xs">Free Tier</Badge>
                                )}
                                {u.free_access_granted && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    <Gift className="w-3 h-3 mr-1" /> Free Access
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <Badge variant={u.role === 'shelter' ? 'default' : u.role === 'vet' ? 'secondary' : 'outline'}>
                            {u.role}
                          </Badge>
                          <Badge variant={u.account_status === 'active' ? 'default' : 'destructive'}>
                            {u.account_status || 'active'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {/* Free Access Controls for Shelters */}
                        {u.role === 'shelter' && (
                          <>
                            {!u.free_access_granted ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => handleGrantFreeAccess(u.user_id, null, 'Admin approved')}
                                title="Grant Free Premium Access"
                              >
                                <Gift className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleRevokeFreeAccess(u.user_id)}
                                title="Revoke Free Access"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedUser(u); setEditDialog(true); }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {(u.role === 'owner' || u.role === 'breeder' || u.role === 'shelter') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewUserPets(u)}
                            title="View Pets"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendLoginEmail(u)}
                          title="Send Login Email"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        {u.account_status !== 'paused' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePauseAccount(u.user_id)}
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleActivateAccount(u.user_id)}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteAccount(u.user_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-center text-stone-500 py-4">No users found. Click Search to load.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ANIMALS TAB (Shelter Animals) */}
          <TabsContent value="animals">
            <Card>
              <CardHeader>
                <CardTitle>Shelter Animals ({animals.length})</CardTitle>
                <CardDescription>Animals registered by shelters</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {animals.slice(0, 50).map((animal) => (
                    <div key={animal.animal_id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{animal.name}</p>
                          <p className="text-sm text-stone-600">{animal.species} - {animal.breed}</p>
                        </div>
                        <Badge>{animal.status}</Badge>
                      </div>
                      {animal.microchip_number && (
                        <p className="text-xs text-stone-500 flex items-center gap-1">
                          <Cpu className="w-3 h-3" />{animal.microchip_number}
                        </p>
                      )}
                      {animal.organization && (
                        <p className="text-xs text-stone-500 mt-2">
                          {animal.organization.organization_name || animal.organization.name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PETS TAB (All Pets with Connections) */}
          <TabsContent value="pets">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>All Pets with Connections ({pets.length})</CardTitle>
                    <CardDescription>Pets registered with owner, breeder, and vet connections</CardDescription>
                  </div>
                  <Button onClick={() => { setCreatePetDialogOpen(true); fetchSelectionData(); }}>
                    <Plus className="w-4 h-4 mr-2" />Add Pet
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pets.map((pet) => (
                    <div key={pet.pet_id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold">{pet.name}</p>
                            <Badge variant="outline">{pet.species}</Badge>
                            <Badge>{pet.status}</Badge>
                            {pet.created_by_admin && (
                              <Badge variant="secondary" className="text-xs">Admin Added</Badge>
                            )}
                          </div>
                          <p className="text-sm text-stone-600">{pet.breed} • {pet.gender} • {pet.color}</p>
                          
                          <div className="grid md:grid-cols-2 gap-2 mt-2 text-xs">
                            {pet.microchip_number && (
                              <p className="flex items-center gap-1"><Cpu className="w-3 h-3" /> Primary: {pet.microchip_number}</p>
                            )}
                            {pet.secondary_microchip && (
                              <p className="flex items-center gap-1"><Cpu className="w-3 h-3" /> Secondary: {pet.secondary_microchip}</p>
                            )}
                          </div>
                          
                          <div className="mt-2 pt-2 border-t text-xs space-y-1">
                            {pet.owner && (
                              <p><strong>Owner:</strong> {pet.owner.name || 'Unknown'} ({pet.owner.email || 'No email'}) - {pet.owner_type || 'Unknown'}</p>
                            )}
                            {pet.breeder && (
                              <p><strong>Breeder:</strong> {pet.breeder.business_name || pet.breeder.name || 'Unknown'}</p>
                            )}
                            {pet.implanter && (
                              <p><strong>Implanter:</strong> {pet.implanter.clinic_name || pet.implanter.name || 'Unknown'}</p>
                            )}
                            {pet.primary_vet && (
                              <p><strong>Primary Vet:</strong> {pet.primary_vet.clinic_name || pet.primary_vet.name || 'Unknown'}</p>
                            )}
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setSelectedPet(pet); setConnectPetDialogOpen(true); fetchSelectionData(); }}
                        >
                          <Link2 className="w-4 h-4 mr-1" />Connect
                        </Button>
                      </div>
                    </div>
                  ))}
                  {pets.length === 0 && (
                    <p className="text-center text-stone-500 py-4">No pets found. Add one using the button above.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MICROCHIPS TAB */}
          <TabsContent value="microchips">
            <Card>
              <CardHeader>
                <CardTitle>Microchip Registry ({microchips.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {microchips.slice(0, 50).map((chip) => (
                    <div key={chip.microchip_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-mono font-semibold">{chip.microchip_number}</p>
                        {chip.owner && (
                          <p className="text-sm text-stone-600">{chip.owner.name || 'Unknown'} ({chip.owner.email || 'No email'})</p>
                        )}
                        {chip.implanter_id && (
                          <p className="text-xs text-stone-500">Implanted: {chip.implant_date}</p>
                        )}
                      </div>
                      <Badge variant={chip.registration_status === 'registered' ? 'default' : 'secondary'}>
                        {chip.registration_status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TRANSACTIONS TAB */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Payment Transactions ({transactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactions.slice(0, 50).map((txn) => (
                    <div key={txn.transaction_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-semibold">${txn.amount} {txn.currency?.toUpperCase()}</p>
                        <p className="text-sm text-stone-600">
                          {txn.type === 'microchip_registration' ? 'Microchip Registration' : 'Subscription'}
                        </p>
                        {txn.user && (
                          <p className="text-xs text-stone-500">{txn.user.name || 'Unknown'} - {txn.user.email || 'No email'}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge variant={txn.status === 'completed' ? 'default' : 'secondary'}>
                          {txn.status}
                        </Badge>
                        <p className="text-xs text-stone-500 mt-1">
                          {new Date(txn.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PARTNERS TAB */}
          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <CardTitle>Partner Registries</CardTitle>
                <CardDescription>Manage connected microchip registries</CardDescription>
              </CardHeader>
              <CardContent>
                {partners.length > 0 ? (
                  <div className="space-y-4">
                    {partners.map((partner) => (
                      <div key={partner.partner_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          {partner.logo_url ? (
                            <img src={partner.logo_url} alt={partner.registry_name} className="w-12 h-12 rounded-lg object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-primary" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-semibold">{partner.registry_name}</h4>
                            <p className="text-sm text-stone-500">{partner.contact_email}</p>
                            <div className="flex gap-2 mt-1 flex-wrap">
                              <Badge variant={partner.status === 'active' ? 'default' : partner.status === 'pending_payment' ? 'secondary' : 'outline'}>
                                {partner.status}
                              </Badge>
                              {partner.free_access_granted ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <Gift className="w-3 h-3 mr-1" /> Free Access
                                </Badge>
                              ) : partner.subscription_status && (
                                <Badge variant={partner.subscription_status === 'paid' || partner.subscription_status === 'free_access' ? 'default' : 'destructive'}>
                                  {partner.subscription_status}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {/* Free Access Controls for Partners */}
                          {!partner.free_access_granted ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleGrantPartnerFreeAccess(partner.partner_id, null, 'Admin approved')}
                              title="Grant Free Partner Access"
                            >
                              <Gift className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleRevokePartnerFreeAccess(partner.partner_id)}
                              title="Revoke Free Access"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          {partner.status === 'pending_payment' && !partner.free_access_granted && partner.subscription_status === 'paid' && (
                            <Button size="sm" onClick={() => handleApprovePartner(partner.partner_id)}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                          )}
                          {partner.status === 'active' && !partner.free_access_granted && (
                            <Button size="sm" variant="destructive" onClick={() => handleSuspendPartner(partner.partner_id)}>
                              <XCircle className="w-4 h-4 mr-1" /> Suspend
                            </Button>
                          )}
                          <a href={partner.website_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline">Visit Site</Button>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-stone-500 py-4">No partner registries yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FEATURE REQUESTS TAB */}
          <TabsContent value="features">
            <Card>
              <CardHeader>
                <CardTitle>Feature Requests</CardTitle>
                <CardDescription>User-submitted feature ideas</CardDescription>
              </CardHeader>
              <CardContent>
                {featureRequests.length > 0 ? (
                  <div className="space-y-4">
                    {featureRequests.map((request) => (
                      <div key={request.request_id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold">{request.title}</h4>
                            <p className="text-sm text-stone-600 mt-1">{request.description}</p>
                            <div className="flex gap-2 mt-2 text-xs text-stone-500">
                              <span>From: {request.email || request.user_name || 'Anonymous'}</span>
                              <span>•</span>
                              <span>{new Date(request.submitted_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 items-center">
                            <Badge variant={
                              request.status === 'completed' ? 'default' :
                              request.status === 'planned' ? 'secondary' :
                              request.status === 'declined' ? 'destructive' : 'outline'
                            }>
                              {request.status}
                            </Badge>
                            <Select defaultValue={request.status} onValueChange={(value) => handleUpdateFeatureRequest(request.request_id, value)}>
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="reviewed">Reviewed</SelectItem>
                                <SelectItem value="planned">Planned</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="declined">Declined</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-stone-500 py-4">No feature requests yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ACTIVITY LOGS TAB */}
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Activity Logs</span>
                  <Button onClick={() => { fetchActivityLogs(); fetchLogsSummary(); }} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />Refresh
                  </Button>
                </CardTitle>
                <CardDescription>All user and admin activity across the platform</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search and Filter */}
                <div className="flex flex-wrap gap-3 p-4 bg-stone-50 rounded-lg">
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      placeholder="Search logs by action, user, details..."
                      value={logSearchQuery}
                      onChange={(e) => setLogSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchLogs()}
                    />
                  </div>
                  <Select value={logRoleFilter} onValueChange={setLogRoleFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="shelter">Shelter</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="breeder">Breeder</SelectItem>
                      <SelectItem value="vet">Vet</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={logActionFilter} onValueChange={setLogActionFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="create">Create</SelectItem>
                      <SelectItem value="update">Update</SelectItem>
                      <SelectItem value="delete">Delete</SelectItem>
                      <SelectItem value="login">Login</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                      <SelectItem value="publish">Publish</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleSearchLogs}>
                    <Search className="w-4 h-4 mr-2" />Search
                  </Button>
                </div>

                {/* Logs Summary */}
                {logsSummary && (
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{logsSummary.recent_24h}</p>
                      <p className="text-xs text-blue-800">Last 24 hours</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{logsSummary.total_admin_logs}</p>
                      <p className="text-xs text-green-800">Admin Actions</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{logsSummary.total_user_logs}</p>
                      <p className="text-xs text-purple-800">User Actions</p>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-600">{activityLogs.length}</p>
                      <p className="text-xs text-orange-800">Showing</p>
                    </div>
                  </div>
                )}

                {/* Logs List */}
                {activityLogs.length > 0 ? (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {activityLogs.map((log, idx) => {
                      // Helper function to safely get user display name
                      const getUserName = () => {
                        if (log.user && typeof log.user === 'object') {
                          return log.user.name || log.user.email || 'Unknown';
                        }
                        if (log.user_email) return log.user_email;
                        if (log.admin && typeof log.admin === 'object') {
                          return log.admin.name || log.admin.email || 'Unknown';
                        }
                        return 'System';
                      };
                      
                      return (
                        <div key={log.log_id || idx} className="flex justify-between items-start p-3 bg-stone-50 rounded-lg hover:bg-stone-100">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={log.log_type === 'admin' ? 'default' : 'secondary'} className="text-xs">
                                {log.log_type === 'admin' ? 'Admin' : (log.user_role || 'User')}
                              </Badge>
                              <span className="font-medium text-sm">
                                {log.action?.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-stone-600">
                              {typeof log.details === 'object' 
                                ? JSON.stringify(log.details) 
                                : (log.details || 'No details')}
                            </p>
                            <p className="text-xs text-stone-500 mt-1">
                              By: {getUserName()}
                              {log.entity_name ? ` • ${log.entity_type || 'Item'}: ${log.entity_name}` : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-stone-400">{log.created_at ? new Date(log.created_at).toLocaleDateString() : '-'}</p>
                            <p className="text-xs text-stone-400">{log.created_at ? new Date(log.created_at).toLocaleTimeString() : '-'}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-stone-500 py-8">No activity logs found. Try adjusting your search filters.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit User Dialog */}
        {selectedUser && (
          <Dialog open={editDialog} onOpenChange={setEditDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={selectedUser.name}
                    onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={selectedUser.email}
                    onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                  />
                </div>
                {(selectedUser.organization_name !== undefined || selectedUser.clinic_name !== undefined || selectedUser.business_name !== undefined) && (
                  <div>
                    <Label>Organization/Business Name</Label>
                    <Input
                      value={selectedUser.organization_name || selectedUser.clinic_name || selectedUser.business_name || ''}
                      onChange={(e) => setSelectedUser({ 
                        ...selectedUser, 
                        organization_name: selectedUser.role === 'shelter' ? e.target.value : selectedUser.organization_name,
                        clinic_name: selectedUser.role === 'vet' ? e.target.value : selectedUser.clinic_name,
                        business_name: selectedUser.role === 'breeder' ? e.target.value : selectedUser.business_name
                      })}
                    />
                  </div>
                )}
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={selectedUser.phone || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                  Update User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Create User Dialog */}
        <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />Add New User (Rescue/Vet/Breeder/Owner)
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label>User Type *</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shelter">Shelter / Rescue</SelectItem>
                    <SelectItem value="vet">Vet Clinic</SelectItem>
                    <SelectItem value="breeder">Breeder</SelectItem>
                    <SelectItem value="owner">Pet Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} />
                </div>
              </div>
              
              {/* Email Login Details Option */}
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <Checkbox 
                  checked={newUser.send_email} 
                  onCheckedChange={(checked) => setNewUser({...newUser, send_email: checked})}
                />
                <div>
                  <Label className="cursor-pointer">Email login details to user</Label>
                  <p className="text-xs text-stone-500">User will receive an email with their login credentials and a temporary password</p>
                </div>
              </div>
              
              {!newUser.send_email && (
                <div>
                  <Label>Password *</Label>
                  <Input 
                    type="password" 
                    value={newUser.password} 
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})} 
                    required={!newUser.send_email}
                    placeholder="Set temporary password manually" 
                  />
                </div>
              )}
              
              {newUser.role === 'shelter' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Organization Name *</Label>
                    <Input value={newUser.organization_name} onChange={(e) => setNewUser({...newUser, organization_name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>License Number</Label>
                    <Input value={newUser.license_number} onChange={(e) => setNewUser({...newUser, license_number: e.target.value})} />
                  </div>
                </div>
              )}

              {newUser.role === 'vet' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Clinic Name *</Label>
                    <Input value={newUser.clinic_name} onChange={(e) => setNewUser({...newUser, clinic_name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>License Number</Label>
                    <Input value={newUser.license_number} onChange={(e) => setNewUser({...newUser, license_number: e.target.value})} />
                  </div>
                  <div>
                    <Label>Specializations</Label>
                    <Input value={newUser.specializations} onChange={(e) => setNewUser({...newUser, specializations: e.target.value})} placeholder="e.g., Surgery, Dentistry" />
                  </div>
                </div>
              )}

              {newUser.role === 'breeder' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Business Name *</Label>
                    <Input value={newUser.business_name} onChange={(e) => setNewUser({...newUser, business_name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Registration Number</Label>
                    <Input value={newUser.registration_number} onChange={(e) => setNewUser({...newUser, registration_number: e.target.value})} />
                  </div>
                  <div>
                    <Label>Breeds Specialized</Label>
                    <Input value={newUser.breeds_specialized} onChange={(e) => setNewUser({...newUser, breeds_specialized: e.target.value})} placeholder="e.g., Golden Retriever, Labrador" />
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Address</Label>
                  <Input value={newUser.address} onChange={(e) => setNewUser({...newUser, address: e.target.value})} />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={newUser.city} onChange={(e) => setNewUser({...newUser, city: e.target.value})} />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={newUser.state} onChange={(e) => setNewUser({...newUser, state: e.target.value})} />
                </div>
                <div>
                  <Label>Zip Code</Label>
                  <Input value={newUser.zip_code} onChange={(e) => setNewUser({...newUser, zip_code: e.target.value})} />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateUserDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">Create User</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Pet Dialog */}
        <Dialog open={createPetDialogOpen} onOpenChange={setCreatePetDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PawPrint className="w-5 h-5" />Add Pet with Connections
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePet} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Pet Name *</Label>
                  <Input value={newPet.name} onChange={(e) => setNewPet({...newPet, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Species *</Label>
                  <Select value={newPet.species} onValueChange={(v) => setNewPet({...newPet, species: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dog">Dog</SelectItem>
                      <SelectItem value="Cat">Cat</SelectItem>
                      <SelectItem value="Bird">Bird</SelectItem>
                      <SelectItem value="Rabbit">Rabbit</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Breed</Label>
                  <Input value={newPet.breed} onChange={(e) => setNewPet({...newPet, breed: e.target.value})} />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={newPet.gender} onValueChange={(v) => setNewPet({...newPet, gender: v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Color</Label>
                  <Input value={newPet.color} onChange={(e) => setNewPet({...newPet, color: e.target.value})} />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input type="date" value={newPet.date_of_birth} onChange={(e) => setNewPet({...newPet, date_of_birth: e.target.value})} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Primary Microchip Number</Label>
                  <Input value={newPet.microchip_number} onChange={(e) => setNewPet({...newPet, microchip_number: e.target.value})} />
                </div>
                <div>
                  <Label>Secondary Microchip</Label>
                  <Input value={newPet.secondary_microchip} onChange={(e) => setNewPet({...newPet, secondary_microchip: e.target.value})} />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Connect to Owner/Shelter/Breeder</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Owner Type</Label>
                    <Select value={newPet.owner_type} onValueChange={(v) => { 
                      setNewPet({...newPet, owner_type: v, owner_id: ''}); 
                      setCreateNewOwner(false); 
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Individual Owner</SelectItem>
                        <SelectItem value="shelter">Shelter/Rescue</SelectItem>
                        <SelectItem value="breeder">Breeder</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* For individual owner type, show search and create new option */}
                  {newPet.owner_type === 'owner' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Select or Create Owner</Label>
                        <Button 
                          type="button" 
                          variant="link" 
                          size="sm"
                          className="text-primary h-auto p-0"
                          onClick={() => {
                            setCreateNewOwner(!createNewOwner);
                            setNewPet({...newPet, owner_id: ''});
                          }}
                        >
                          {createNewOwner ? '← Select Existing' : '+ Create New Owner'}
                        </Button>
                      </div>
                      
                      {!createNewOwner ? (
                        <Select value={newPet.owner_id} onValueChange={(v) => setNewPet({...newPet, owner_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Choose existing owner..." /></SelectTrigger>
                          <SelectContent>
                            {owners.map(o => (
                              <SelectItem key={o.user_id} value={o.user_id}>{o.name} ({o.email})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="secondary" className="w-full justify-center py-2">Creating New Owner (see form below)</Badge>
                      )}
                    </div>
                  )}
                  
                  {/* For shelter/breeder, just show dropdown */}
                  {newPet.owner_type === 'shelter' && (
                    <div>
                      <Label>Select Shelter</Label>
                      <Select value={newPet.owner_id} onValueChange={(v) => setNewPet({...newPet, owner_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Choose..." /></SelectTrigger>
                        <SelectContent>
                          {shelters.map(s => (
                            <SelectItem key={s.user_id} value={s.user_id}>{s.organization_name || s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {newPet.owner_type === 'breeder' && (
                    <div>
                      <Label>Select Breeder</Label>
                      <Select value={newPet.owner_id} onValueChange={(v) => setNewPet({...newPet, owner_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Choose..." /></SelectTrigger>
                        <SelectContent>
                          {breeders.map(b => (
                            <SelectItem key={b.user_id} value={b.user_id}>{b.business_name || b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {/* NEW OWNER FORM - Shows when createNewOwner is true */}
                {createNewOwner && newPet.owner_type === 'owner' && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <h5 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />New Owner Details
                    </h5>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Name *</Label>
                        <Input 
                          value={newOwnerForm.name} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, name: e.target.value})} 
                          required={createNewOwner}
                          placeholder="Owner's full name"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Email *</Label>
                        <Input 
                          type="email"
                          value={newOwnerForm.email} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, email: e.target.value})} 
                          required={createNewOwner}
                          placeholder="owner@email.com"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Phone</Label>
                        <Input 
                          value={newOwnerForm.phone} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, phone: e.target.value})} 
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Temporary Password</Label>
                        <Input 
                          type="text"
                          value={newOwnerForm.password} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, password: e.target.value})} 
                          placeholder="tempPassword123!"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Address</Label>
                        <Input 
                          value={newOwnerForm.address} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, address: e.target.value})} 
                          placeholder="Street address"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">City</Label>
                        <Input 
                          value={newOwnerForm.city} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, city: e.target.value})} 
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">State</Label>
                        <Input 
                          value={newOwnerForm.state} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, state: e.target.value})} 
                          placeholder="State"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Zip Code</Label>
                        <Input 
                          value={newOwnerForm.zip_code} 
                          onChange={(e) => setNewOwnerForm({...newOwnerForm, zip_code: e.target.value})} 
                          placeholder="12345"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      ✓ This owner will be created when you submit the form. They&apos;ll appear on their dashboard immediately.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Microchip Implanter (Vet)</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Implanter (Vet)</Label>
                    <Select value={newPet.implanter_id} onValueChange={(v) => setNewPet({...newPet, implanter_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select vet..." /></SelectTrigger>
                      <SelectContent>
                        {vets.map(v => (
                          <SelectItem key={v.user_id} value={v.user_id}>{v.clinic_name || v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Implant Date</Label>
                    <Input type="date" value={newPet.implant_date} onChange={(e) => setNewPet({...newPet, implant_date: e.target.value})} />
                  </div>
                </div>
              </div>

              {newPet.owner_type !== 'breeder' && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Original Breeder (Optional)</h4>
                  <div>
                    <Label>Breeder</Label>
                    <Select value={newPet.breeder_id} onValueChange={(v) => setNewPet({...newPet, breeder_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select breeder..." /></SelectTrigger>
                      <SelectContent>
                        {breeders.map(b => (
                          <SelectItem key={b.user_id} value={b.user_id}>{b.business_name || b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreatePetDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">Create Pet</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Connect Pet Dialog */}
        <Dialog open={connectPetDialogOpen} onOpenChange={setConnectPetDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5" />Update Pet Connections
              </DialogTitle>
            </DialogHeader>
            {selectedPet && (
              <form onSubmit={handleConnectPet} className="space-y-4">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="font-semibold">{selectedPet.name}</p>
                  <p className="text-sm text-stone-600">{selectedPet.species} • {selectedPet.breed}</p>
                </div>

                <div>
                  <Label>Change Owner</Label>
                  <div className="flex gap-2">
                    <Select value={petConnection.owner_type} onValueChange={(v) => setPetConnection({...petConnection, owner_type: v})}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="shelter">Shelter</SelectItem>
                        <SelectItem value="breeder">Breeder</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={petConnection.owner_id} onValueChange={(v) => setPetConnection({...petConnection, owner_id: v})}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select new owner..." /></SelectTrigger>
                      <SelectContent>
                        {petConnection.owner_type === 'owner' && owners.map(o => (
                          <SelectItem key={o.user_id} value={o.user_id}>{o.name}</SelectItem>
                        ))}
                        {petConnection.owner_type === 'shelter' && shelters.map(s => (
                          <SelectItem key={s.user_id} value={s.user_id}>{s.organization_name || s.name}</SelectItem>
                        ))}
                        {petConnection.owner_type === 'breeder' && breeders.map(b => (
                          <SelectItem key={b.user_id} value={b.user_id}>{b.business_name || b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Update Implanter/Vet</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={petConnection.implanter_id} onValueChange={(v) => setPetConnection({...petConnection, implanter_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select vet..." /></SelectTrigger>
                      <SelectContent>
                        {vets.map(v => (
                          <SelectItem key={v.user_id} value={v.user_id}>{v.clinic_name || v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="date" placeholder="Implant date" value={petConnection.implant_date} onChange={(e) => setPetConnection({...petConnection, implant_date: e.target.value})} />
                  </div>
                </div>

                <div>
                  <Label>Primary Vet</Label>
                  <Select value={petConnection.vet_id} onValueChange={(v) => setPetConnection({...petConnection, vet_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select primary vet..." /></SelectTrigger>
                    <SelectContent>
                      {vets.map(v => (
                        <SelectItem key={v.user_id} value={v.user_id}>{v.clinic_name || v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Original Breeder</Label>
                  <Select value={petConnection.breeder_id} onValueChange={(v) => setPetConnection({...petConnection, breeder_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select breeder..." /></SelectTrigger>
                    <SelectContent>
                      {breeders.map(b => (
                        <SelectItem key={b.user_id} value={b.user_id}>{b.business_name || b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setConnectPetDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90">Update Connections</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* USER PETS DIALOG */}
        <Dialog open={userPetsDialogOpen} onOpenChange={setUserPetsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PawPrint className="w-5 h-5" />
                Pets for {selectedUserForPets?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Owner Pets */}
              {selectedUserPets.pets?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Registered Pets ({selectedUserPets.pets.length})
                  </h4>
                  <div className="grid gap-3">
                    {selectedUserPets.pets.map(pet => (
                      <div key={pet.pet_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                            {pet.photos?.length > 0 ? (
                              <img src={pet.photos[0]} alt={pet.name} className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <span className="text-xl">{pet.species === 'Dog' ? '🐕' : pet.species === 'Cat' ? '🐈' : '🐾'}</span>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{pet.name}</p>
                            <p className="text-sm text-stone-600">{pet.species} • {pet.breed || 'Unknown breed'}</p>
                            {pet.microchip_number && (
                              <p className="text-xs text-stone-500 flex items-center gap-1">
                                <Cpu className="w-3 h-3" /> {pet.microchip_number}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant={pet.status === 'lost' ? 'destructive' : 'outline'}>
                            {pet.status || 'active'}
                          </Badge>
                          <Button size="sm" variant="outline" onClick={() => handleEditPet(pet)}>
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shelter Animals */}
              {selectedUserPets.shelter_animals?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Shelter Animals ({selectedUserPets.shelter_animals.length})
                  </h4>
                  <div className="grid gap-3">
                    {selectedUserPets.shelter_animals.map(animal => (
                      <div key={animal.animal_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                            {animal.photos?.length > 0 ? (
                              <img src={animal.photos[0]} alt={animal.name} className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <span className="text-xl">{animal.species === 'Dog' ? '🐕' : animal.species === 'Cat' ? '🐈' : '🐾'}</span>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{animal.name}</p>
                            <p className="text-sm text-stone-600">{animal.species} • {animal.breed || 'Unknown'}</p>
                            {animal.microchip_number && (
                              <p className="text-xs text-stone-500 flex items-center gap-1">
                                <Cpu className="w-3 h-3" /> {animal.microchip_number}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant={animal.status === 'available' ? 'default' : 'secondary'}>
                            {animal.status}
                          </Badge>
                          <Button size="sm" variant="outline" onClick={() => handleEditPet({...animal, pet_id: animal.animal_id})}>
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Breeder Animals */}
              {selectedUserPets.breeder_animals?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Stethoscope className="w-4 h-4" /> Breeder Animals ({selectedUserPets.breeder_animals.length})
                  </h4>
                  <div className="grid gap-3">
                    {selectedUserPets.breeder_animals.map(animal => (
                      <div key={animal.animal_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                            <span className="text-xl">🐾</span>
                          </div>
                          <div>
                            <p className="font-semibold">{animal.name}</p>
                            <p className="text-sm text-stone-600">{animal.species} • {animal.breed || 'Unknown'}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Badge>{animal.status || 'available'}</Badge>
                          <Button size="sm" variant="outline" onClick={() => handleEditPet({...animal, pet_id: animal.animal_id})}>
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedUserPets.total === 0 && (
                <div className="text-center py-8 text-stone-500">
                  <PawPrint className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No pets associated with this user</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setUserPetsDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* EDIT PET DIALOG */}
        <Dialog open={editPetDialogOpen} onOpenChange={setEditPetDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Pet - {editingPet?.name}</DialogTitle>
            </DialogHeader>
            
            {editingPet && (
              <form onSubmit={handleSavePet} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input 
                      value={editingPet.name || ''} 
                      onChange={(e) => setEditingPet({...editingPet, name: e.target.value})} 
                    />
                  </div>
                  <div>
                    <Label>Species</Label>
                    <Select value={editingPet.species || 'Dog'} onValueChange={(v) => setEditingPet({...editingPet, species: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Dog">Dog</SelectItem>
                        <SelectItem value="Cat">Cat</SelectItem>
                        <SelectItem value="Bird">Bird</SelectItem>
                        <SelectItem value="Rabbit">Rabbit</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Breed</Label>
                    <Input 
                      value={editingPet.breed || ''} 
                      onChange={(e) => setEditingPet({...editingPet, breed: e.target.value})} 
                    />
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Input 
                      value={editingPet.color || ''} 
                      onChange={(e) => setEditingPet({...editingPet, color: e.target.value})} 
                    />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select value={editingPet.gender || ''} onValueChange={(v) => setEditingPet({...editingPet, gender: v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={editingPet.status || 'active'} onValueChange={(v) => setEditingPet({...editingPet, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="adopted">Adopted</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                        <SelectItem value="deceased">Deceased</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {editingPet.microchip_number && (
                  <div className="bg-stone-100 p-3 rounded-lg">
                    <Label className="text-xs text-stone-500">Microchip (Read Only)</Label>
                    <p className="font-mono">{editingPet.microchip_number}</p>
                  </div>
                )}

                <div>
                  <Label>Notes</Label>
                  <Textarea 
                    value={editingPet.notes || editingPet.medical_notes || ''} 
                    onChange={(e) => setEditingPet({...editingPet, notes: e.target.value})} 
                    rows={3}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditPetDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Save Changes</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* ============ SEND LOGIN EMAIL DIALOG (mailto: approach) ============ */}
        <Dialog open={loginEmailDialogOpen} onOpenChange={setLoginEmailDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Send Login Credentials
              </DialogTitle>
              <CardDescription>
                Send login details to {loginEmailData?.user_name}
              </CardDescription>
            </DialogHeader>
            
            {loginEmailData && (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="font-semibold text-green-800">Credentials Generated!</p>
                  <p className="text-sm text-green-600">Now send the login details via your email</p>
                </div>

                <div className="bg-stone-50 p-4 rounded-lg space-y-2">
                  <p className="text-sm"><strong>Name:</strong> {loginEmailData.user_name}</p>
                  <p className="text-sm"><strong>Email:</strong> {loginEmailData.user_email}</p>
                  <p className="text-sm"><strong>Role:</strong> {loginEmailData.role}</p>
                  <p className="text-sm"><strong>Temp Password:</strong> <code className="bg-white px-2 py-1 rounded">{loginEmailData.temp_password}</code></p>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Option 1: Click to open your email</p>
                  <a 
                    href={getLoginMailtoLink()} 
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-3 rounded-lg font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Mail className="w-4 h-4 inline mr-2" />
                    Open Email App
                  </a>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Option 2: Copy credentials and send manually</p>
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const text = `Login Details for ${loginEmailData.user_name}:\n\nEmail: ${loginEmailData.user_email}\nTemporary Password: ${loginEmailData.temp_password}\nLogin URL: ${loginEmailData.login_url}\n\nPlease change your password after logging in.`;
                      navigator.clipboard.writeText(text);
                      toast.success('Credentials copied to clipboard!');
                    }}
                  >
                    Copy All Details
                  </Button>
                </div>

                <p className="text-xs text-stone-500 text-center">
                  The user will need to change their password after first login.
                </p>

                <DialogFooter>
                  <Button type="button" onClick={() => { setLoginEmailDialogOpen(false); setLoginEmailData(null); }}>
                    Done
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
